// Shared types every package uses. Designed to be the single source of truth
// so sub-agents implementing input/microgames slot into stable signatures.

export interface Point {
  x: number;
  y: number;
}

// ─── Input frames ──────────────────────────────────────────────────────

export interface FaceFrame {
  /** Did MediaPipe see a face this frame? */
  visible: boolean;
  /** All 478 face-mesh landmarks in canvas pixel space (top-left origin). */
  landmarks: Point[];
  /** Inner-lip ring polygon (20 points) — used as the chomp hitbox. */
  innerLip: Point[];
  /** Outer-lip ring polygon (20 points) — used for drama-mask rendering. */
  outerLip: Point[];
  /** True when mouth-gap / face-height > 0.045 (with hysteresis). */
  mouthOpen: boolean;
  /** Continuous 0..1 mouth-openness; raw ratio clamped. */
  mouthOpenRatio: number;
  /** Edge-trigger: true on the frame mouth transitions from closed→open. */
  biteEdge: boolean;
  /** mouth-width / face-height > 0.42 (with hysteresis). */
  smiling: boolean;
  /** Signed roll angle in radians (positive = head tilted to player's right). */
  headTilt: number;
  /** Signed yaw angle in radians (positive = head turned to player's right). */
  headYaw: number;
}

export interface HandFrame {
  visible: boolean;
  /** 21 landmarks in canvas px (MediaPipe Hands index). */
  landmarks: Point[];
  /** Wrist (landmark 0) velocity in px/s. */
  wristVelocity: Point;
  /** True on the frame a fast swipe completed. */
  swipeEvent: boolean;
  /** True on the frame thumb+index pinched. */
  pinchEvent: boolean;
  /** Index fingertip projected onto canvas, null if hand not pointing. */
  pointTarget: Point | null;
  gesture: 'open' | 'fist' | 'point' | 'peace' | 'unknown';
}

export interface HandsFrame {
  left: HandFrame;
  right: HandFrame;
}

export interface PoseFrame {
  visible: boolean;
  /** 33 pose landmarks in canvas px (MediaPipe Pose index). */
  landmarks: Point[];
  /** Signed shoulder-line tilt in radians. */
  shoulderTilt: number;
  /** 0 = standing, 1 = full squat; calibrated against first second. */
  squatLevel: number;
  /** True on the frame a jump was detected. */
  jumpEvent: boolean;
  armRaiseLeft: boolean;
  armRaiseRight: boolean;
  /** Total absolute landmark delta since last frame (motion magnitude). */
  motionDelta: number;
}

export interface InputFrame {
  timestamp: number;
  /** Canvas dimensions at frame capture time (so px coords are unambiguous). */
  videoWidth: number;
  videoHeight: number;
  face: FaceFrame;
  hands: HandsFrame;
  pose: PoseFrame;
}

// ─── Microgame contract ────────────────────────────────────────────────

export type InputChannel = 'face' | 'hands' | 'pose';

export interface GameContext {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  /** Audio facade (procedural SFX) — populated by web shell. */
  audio: GameAudio;
  /** Seeded RNG for daily mode, shared RNG for endless. */
  rng: () => number;
}

export interface GameAudio {
  beep(): void;
  pop(): void;
  thud(): void;
  win(): void;
  lose(): void;
}

export interface MicrogameResult {
  microgame: string;
  won: boolean;
  score: number;
  detail?: Record<string, number>;
}

export interface Microgame {
  /** Unique key; matches the spec filename (e.g. "01-chomp"). */
  name: string;
  /** Total duration in seconds. */
  durationSec: number;
  /** Which input channels MediaPipe must be running for this microgame. */
  requires: { face?: boolean; hands?: boolean; pose?: boolean };
  /** Called once before the first tick. */
  init(ctx: GameContext): void;
  /** Called every rAF frame with dt seconds since last tick. */
  tick(dt: number, frame: InputFrame): void;
  /** Called every rAF frame after tick; draws the microgame onto the canvas. */
  render(ctx: CanvasRenderingContext2D): void;
  /** Called when durationSec elapses or microgame ends early. */
  end(): MicrogameResult;
}

// ─── Run result ────────────────────────────────────────────────────────

export interface RunResult {
  totalScore: number;
  winCount: number;
  microgames: MicrogameResult[];
  durationSec: number;
}

// ─── Empty frame factories (handy for stubs/tests) ─────────────────────

export const emptyFace = (): FaceFrame => ({
  visible: false,
  landmarks: [],
  innerLip: [],
  outerLip: [],
  mouthOpen: false,
  mouthOpenRatio: 0,
  biteEdge: false,
  smiling: false,
  headTilt: 0,
  headYaw: 0,
});

export const emptyHand = (): HandFrame => ({
  visible: false,
  landmarks: [],
  wristVelocity: { x: 0, y: 0 },
  swipeEvent: false,
  pinchEvent: false,
  pointTarget: null,
  gesture: 'unknown',
});

export const emptyPose = (): PoseFrame => ({
  visible: false,
  landmarks: [],
  shoulderTilt: 0,
  squatLevel: 0,
  jumpEvent: false,
  armRaiseLeft: false,
  armRaiseRight: false,
  motionDelta: 0,
});

export const emptyFrame = (
  videoWidth = 0,
  videoHeight = 0,
  timestamp = 0,
): InputFrame => ({
  timestamp,
  videoWidth,
  videoHeight,
  face: emptyFace(),
  hands: { left: emptyHand(), right: emptyHand() },
  pose: emptyPose(),
});
